import { DOCUMENT_HOSTS } from '@/utils/constants';

import type { TSource } from './types';

const DOCUMENT_PATH_PREFIX = '/docs/';
const DOCUMENT_EXTENSION = '.pdf';
const CITED_PAGE_PARAMETER = 'page';
const FIRST_PAGE = 1;
const DIGITS_ONLY = /^\d+$/;
const PATH_SEPARATOR = /[/\\]/;

/**
 * Parses an href relative to the current origin, or returns null when it is not a URL.
 */
const parseHref = (href: string): URL | null => {
  try {
    return new URL(href, window.location.origin);
  } catch {
    return null;
  }
};

/**
 * Tells whether a URL points at the app's own origin, or at a known deployment over https.
 */
const isDocumentOrigin = (url: URL) =>
  url.origin === window.location.origin || (url.protocol === 'https:' && DOCUMENT_HOSTS.includes(url.host));

/**
 * Decodes the path segments, or returns null when one is malformed or hides a separator.
 *
 * `URL` has already resolved `..` and `%2e%2e` segments, so a traversal out of /docs/ fails the prefix check. An
 * encoded separator (`..%2f`) survives parsing, though, and the server behind /docs could still decode it.
 */
const decodePathSegments = (pathname: string): string[] | null => {
  try {
    const segments = pathname.split('/').map(decodeURIComponent);
    return segments.some((segment) => PATH_SEPARATOR.test(segment)) ? null : segments;
  } catch {
    return null;
  }
};

/**
 * Reads the cited page from a `#page=N` fragment, which may carry other parameters. Missing, zero or not a
 * whole number means the first page.
 */
const toCitedPage = (hash: string): number => {
  const page = new URLSearchParams(hash.slice(1)).get(CITED_PAGE_PARAMETER) ?? '';
  return DIGITS_ONLY.test(page) ? Math.max(FIRST_PAGE, Number(page)) : FIRST_PAGE;
};

/**
 * Derives a readable title from a document's file name: `BA_eTGS_2026.pdf` becomes `BA eTGS 2026`.
 */
const toTitleFromFileName = (fileName: string) =>
  fileName.slice(0, -DOCUMENT_EXTENSION.length).replace(/_/g, ' ').trim();

/**
 * Reads a link in an assistant answer as a Source (FIBOT-1747 D6–D9): a PDF under /docs/ on the app's own origin
 * or, over https, on a known deployment. The host is dropped so the Document always loads from the current origin.
 *
 * Returns null for every other link, which then keeps its new-tab behaviour.
 */
export const resolveSource = (href: string | undefined, linkText: string): TSource | null => {
  const url = href ? parseHref(href) : null;
  if (!href || !url || !isDocumentOrigin(url)) return null;

  const { pathname, search, hash } = url;
  if (!pathname.startsWith(DOCUMENT_PATH_PREFIX) || !pathname.toLowerCase().endsWith(DOCUMENT_EXTENSION)) return null;

  const segments = decodePathSegments(pathname);
  if (!segments) return null;

  // INFO: The original href is deliberately not carried along. Everything downstream — including
  // "Open in new tab" — must use the same-origin `documentSrc`, because an answer may cite the
  // production host while the app runs on staging (FIBOT-1747 D8).
  return {
    documentSrc: `${pathname}${search}`,
    citedPage: toCitedPage(hash),
    title: linkText.trim() || toTitleFromFileName(segments[segments.length - 1]),
  };
};
